/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  Memoizes a selector the relies on state and routeProps
 */

import { Tail } from '@zcode/utility-types'
import { RouteComponentProps } from 'react-router-dom'
import { routePropsEqual } from './helpers'
import { memoizeSelector } from './memoizeSelector'
import { MaybeNoMatchRouteProps, MaybeRouteProps, Selector } from './types'

type ArgsDefinitely = [object | undefined, RouteComponentProps]
type Args = [
  object | undefined,
  RouteComponentProps | MaybeNoMatchRouteProps | MaybeRouteProps | undefined,
  ...unknown[],
]

const otherArgsEqual = <A extends Args>(args?: Tail<A>, prevArgs?: Tail<A>) => {
  if (!args && !prevArgs) {
    return true
  }
  if (!args || !prevArgs) {
    return false
  }
  const [routeProps] = args
  const [prevRouteProps] = prevArgs
  return routePropsEqual(routeProps, prevRouteProps)
}

// Custom version of memoizeSelector that ignores ownProps except for the react-router location.
export const memoizeStateByRouteProps = <
  Result,
  A extends Args = ArgsDefinitely,
>(
  selector: Selector<A, Result>
) => memoizeSelector(selector, { otherArgsEqual })
